import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { useEffect, useState } from 'react';
import { currencyService } from '../services/currencyService';

const KlarnaInfo = () => {
  const [, setCountry] = useState(currencyService.getCountry());

  useEffect(() => {
    const unsubscribe = currencyService.subscribe((nextCountry) => {
      setCountry(nextCountry);
    });

    return unsubscribe;
  }, []);

  const showKlarna = currencyService.showKlarna();
  const formatPrice = (price: number) => currencyService.formatPrice(price);
  const total = 1800;
  const installment = currencyService.getKlarnaInstallmentAmount(total);

  return (
    <>
      <Helmet>
        <title>Qu'est-ce que Klarna ? - Couteau Suisse Digital</title>
        <meta
          name="description"
          content="Decouvrez comment fonctionne l option Klarna en 3x sur les projets eligibles de Couteau Suisse Digital."
        />
        <script async src="https://cdn.klarna.com/1.0/code/client/all.js"></script>
      </Helmet>

      <main className="min-h-screen py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="bg-white rounded-2xl shadow-xl p-8"
          >
            <h1 className="text-3xl font-bold mb-8">Qu'est-ce que Klarna ?</h1>

            {/* Klarna Info Widget */}
            {showKlarna && (
              <div className="mb-12">
                <klarna-placement
                  data-key="info-page"
                  data-locale="fr-FR"
                ></klarna-placement>
              </div>
            )}

            {/* Additional Information */}
            <div className="space-y-8">
              <section>
                <h2 className="text-xl font-semibold mb-4">Comment fonctionne le paiement en 3x ?</h2>
                <div className="bg-gray-50 rounded-xl p-6">
                  <p className="text-gray-600 mb-4">
                    Avec Klarna, certains projets eligibles peuvent etre payes en 3 versements egaux sans frais supplementaires, dans la limite de {currencyService.getKlarnaMaxAmountEUR()} EUR :
                  </p>
                  <ul className="space-y-2 text-gray-600">
                    <li className="flex items-center gap-2">
                      <span className="text-neon-orange">1.</span>
                      Premier versement à la commande
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="text-neon-orange">2.</span>
                      Deuxième versement après 30 jours
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="text-neon-orange">3.</span>
                      Dernier versement après 60 jours
                    </li>
                  </ul>
                </div>
              </section>

              <section>
                <h2 className="text-xl font-semibold mb-4">Exemple de paiement</h2>
                <div className="bg-gray-50 rounded-xl p-6">
                  <p className="text-gray-600 mb-4">Pour un achat de {formatPrice(total)} :</p>
                  <ul className="space-y-2 text-gray-600">
                    <li>• 1er versement : {formatPrice(installment)} à la commande</li>
                    <li>• 2 mensualités de {formatPrice(installment)}</li>
                    <li>• Coût total : {formatPrice(total)} (TAEG 0%)</li>
                  </ul>
                </div>
              </section>

              {!showKlarna && (
                <section>
                  <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-6 text-yellow-900">
                    Klarna n'est pas disponible dans votre pays/devise actuelle.
                  </div>
                </section>
              )}

              <section>
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 text-amber-900">
                  Au-dela de {currencyService.getKlarnaMaxAmountEUR()} EUR, nous orientons le projet vers un paiement classique, un devis ou une solution plus adaptee.
                </div>
              </section>

              <section>
                <h2 className="text-xl font-semibold mb-4">Avantages de Klarna</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-gray-50 rounded-xl p-6">
                    <h3 className="font-semibold mb-2">Simple</h3>
                    <p className="text-gray-600">
                      Processus de paiement rapide et intuitif, sans paperasse.
                    </p>
                  </div>
                  <div className="bg-gray-50 rounded-xl p-6">
                    <h3 className="font-semibold mb-2">Sans frais</h3>
                    <p className="text-gray-600">
                      Aucun frais supplémentaire, vous ne payez que le prix de votre achat.
                    </p>
                  </div>
                  <div className="bg-gray-50 rounded-xl p-6">
                    <h3 className="font-semibold mb-2">Sécurisé</h3>
                    <p className="text-gray-600">
                      Paiement 100% sécurisé avec les dernières technologies de cryptage.
                    </p>
                  </div>
                  <div className="bg-gray-50 rounded-xl p-6">
                    <h3 className="font-semibold mb-2">Flexible</h3>
                    <p className="text-gray-600">
                      Gérez vos paiements facilement depuis l'application Klarna.
                    </p>
                  </div>
                </div>
              </section>

              <section>
                <h2 className="text-xl font-semibold mb-4">Pays où Klarna est disponible</h2>
                <div className="bg-gray-50 rounded-xl p-6">
                  <p className="text-gray-600 mb-4">
                    Le paiement en 3x sans frais avec Klarna est disponible dans les pays suivants :
                  </p>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                    <div className="flex items-center gap-2">
                      <span className="text-neon-orange">✓</span>
                      <span>Allemagne</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-neon-orange">✓</span>
                      <span>Autriche</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-neon-orange">✓</span>
                      <span>Belgique</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-neon-orange">✓</span>
                      <span>Danemark</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-neon-orange">✓</span>
                      <span>Espagne</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-neon-orange">✓</span>
                      <span>Finlande</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-neon-orange">✓</span>
                      <span>France</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-neon-orange">✓</span>
                      <span>Irlande</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-neon-orange">✓</span>
                      <span>Italie</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-neon-orange">✓</span>
                      <span>Norvège</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-neon-orange">✓</span>
                      <span>Pays-Bas</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-neon-orange">✓</span>
                      <span>Portugal</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-neon-orange">✓</span>
                      <span>Royaume-Uni</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-neon-orange">✓</span>
                      <span>Suède</span>
                    </div>
                  </div>
                </div>
              </section>
            </div>
          </motion.div>
        </div>
      </main>
    </>
  );
};

export default KlarnaInfo;
